import { FC, useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { Box, CircularProgress, Link, Paper, Typography, alpha, useTheme } from '@mui/material'
import { DialogChangePassword } from '../../../Components/Dialog/variants/dialog-change-password'
import { Placeholder } from '../../../Components/Placeholder/placeholder.component'
import { useToast } from '../../../Hooks/ToasContext'
import { ConfirmPasswordResetUseCase } from '../../../Usecase/Auth/confirm-password-reset-usecase'
import { ValidatePasswordResetTokenUseCase } from '../../../Usecase/Auth/validate-password-reset-token-usecase'
import { adminVersion } from '../../../Services/api'

interface IResetPasswordPageProps {
  confirmPasswordResetUseCase: ConfirmPasswordResetUseCase
  validatePasswordResetTokenUseCase: ValidatePasswordResetTokenUseCase
}

const ResetPasswordPage: FC<IResetPasswordPageProps> = ({
  confirmPasswordResetUseCase,
  validatePasswordResetTokenUseCase,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')?.trim() || ''
  const [loading, setLoading] = useState(false)
  const [validatingToken, setValidatingToken] = useState(!!token)
  const [tokenError, setTokenError] = useState('')

  useEffect(() => {
    let active = true

    const validateToken = async () => {
      if (!token) {
        setValidatingToken(false)
        return
      }

      setValidatingToken(true)
      setTokenError('')

      try {
        const result = await validatePasswordResetTokenUseCase.handle(token)

        if (!active) return

        if (result.isFailure) {
          const err = result.error as { error?: string; message?: string; statusCode?: number }
          setTokenError(String(err?.error || err?.message || 'Link expirado ou inválido. Solicite uma nova recuperação de senha.'))
          return
        }

        const value = result.getValue()
        if (!value?.valid) {
          setTokenError(value?.message || 'Link expirado ou inválido. Solicite uma nova recuperação de senha.')
        }
      } catch {
        if (active) {
          setTokenError('Não foi possível validar o link de recuperação. Tente novamente.')
        }
      } finally {
        if (active) {
          setValidatingToken(false)
        }
      }
    }

    validateToken()

    return () => {
      active = false
    }
  }, [token, validatePasswordResetTokenUseCase])

  const handleConfirm = async ({ password }: { password: string }) => {
    setLoading(true)

    try {
      const result = await confirmPasswordResetUseCase.handle({
        token,
        newPassword: password,
      })

      if (result.isFailure) {
        const err = result.error as { error?: string; message?: string; statusCode?: number }
        showToast(String(err?.error || err?.message || 'Não foi possível redefinir a senha.'), 'error')
        return
      }

      showToast('Senha redefinida com sucesso. Faça login com a nova senha.', 'success')
      navigate('/signin', { replace: true })
    } catch {
      showToast('Não foi possível redefinir a senha. Tente novamente.', 'error')
    } finally {
      setLoading(false)
    }
  }

  if (!token || tokenError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          backgroundColor: '#eef2e6',
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 480, width: '100%', borderRadius: 3 }}>
          <Placeholder
            description={tokenError || 'Link de recuperação inválido. Verifique o e-mail recebido.'}
            Bottom={() => (
              <Link component={RouterLink} to="/signin" sx={{ mt: 2 }}>
                Voltar para o login
              </Link>
            )}
          />
        </Paper>
      </Box>
    )
  }

  if (validatingToken) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          backgroundColor: '#eef2e6',
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 480, width: '100%', borderRadius: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Validando link de recuperação...
          </Typography>
        </Paper>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#eef2e6',
        backgroundImage: `
          radial-gradient(circle at 15% 20%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 42%)
        `,
      }}
    >
      <DialogChangePassword
        open
        forceReset
        isLoading={loading}
        title="Redefinir senha"
        description="Defina uma nova senha para acessar a plataforma. Ela deve ter no mínimo 6 caracteres, uma letra maiúscula e um caractere especial."
        onConfirm={handleConfirm}
        onCancel={() => navigate('/signin')}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ position: 'fixed', bottom: 16, left: 0, right: 0, textAlign: 'center' }}
      >
        Versão {adminVersion}
      </Typography>
    </Box>
  )
}

export { ResetPasswordPage }
